/**
 * Formatted proposal text — storage, sanitising, and the two conversions either side of it.
 *
 * A rule proposal is a legal document that ten people argue over and one of them later has to
 * read out loud. It has sections, it has lists, it cites things, and it increasingly has tables —
 * the keeper grid and the apron thresholds are tables in every draft anyone has ever circulated.
 *
 * WHY THIS IS HAND-WRITTEN AND NOT A LIBRARY. The body is entered by ten people and rendered to
 * all of them, so it is untrusted input on a shared screen. The usual answer is an editor
 * framework plus a sanitiser, and both are large: the offline single-file build is the draft-night
 * fallback and every kilobyte in it is a kilobyte that has to survive somebody's hotel wifi.
 *
 * THE GUARANTEE, AND HOW IT CHANGED. This began as an allowlist with NO attributes at all, which
 * is the strongest form of the promise and the easiest to audit. Links and tables cannot be
 * expressed without attributes, so the promise is now narrower and has to be stated precisely:
 *
 *   - `href` on `<a>`, and ONLY after `safeHref` has approved the scheme. http, https and mailto
 *     survive; everything else — `javascript:`, `data:`, `vbscript:`, and any scheme nobody has
 *     thought of — is refused and the link is unwrapped to its text.
 *   - `colspan` and `rowspan` on `<th>` and `<td>`, and ONLY as one- or two-digit integers.
 *   - Nothing else, on anything. No `style`, no `on*`, no `src`, no `class`, no `id`.
 *
 * Every value that survives is re-serialised by this file through `escapeAttr`, never copied
 * through as written, so an attribute cannot break out of its own quotes.
 *
 * Unknown tags are unwrapped rather than escaped, so pasting from Google Docs yields text rather
 * than a screenful of markup. `script` and `style` are the exceptions: their CONTENT is dropped
 * too, because unwrapping a script tag would paste its source into the document as visible prose.
 *
 * It is deliberately free of DOM. `DOMParser` would be less code and is not available in the test
 * runner, which runs in node — and a sanitiser whose tests cannot run is not a sanitiser.
 *
 * THE STORED FORM IS CANONICAL. `strong` becomes `b`, `em` becomes `i`, `strike` and `del` become
 * `s`, `ins` becomes `u`, `div` becomes `p`. Browsers disagree about which of each pair
 * `execCommand` emits, and without this the same keystroke on two managers' laptops produces two
 * different diffs.
 */

/** What survives. */
const ALLOWED = new Set([
  'p', 'br', 'b', 'i', 'u', 's', 'ul', 'ol', 'li', 'a',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
]);

/** The four inline marks, in a fixed order so the same paste always produces the same markup. */
const MARKS = ['b', 'i', 'u', 's'] as const;
type Mark = typeof MARKS[number];

/** Tags that carry a mark by themselves. See "the stored form is canonical" above. */
const TAG_MARK: Record<string, Mark> = {
  b: 'b', strong: 'b', i: 'i', em: 'i', u: 'u', ins: 'u', s: 's', strike: 's', del: 's',
};

/** Structure, as opposed to emphasis. `div` becomes `p`; browsers disagree about which they emit. */
const STRUCT: Record<string, string> = {
  p: 'p', div: 'p', ul: 'ul', ol: 'ol', li: 'li', br: 'br', a: 'a',
  table: 'table', thead: 'thead', tbody: 'tbody', tfoot: 'tfoot', tr: 'tr', th: 'th', td: 'td',
};

/** Tags whose text content is dropped along with the tag. */
const OPAQUE = new Set(['script', 'style']);

/** Blocks close an open block of the same kind rather than nesting inside it. */
const BLOCK = new Set(['p', 'li']);

const CELL = new Set(['th', 'td']);
const SECTION = new Set(['thead', 'tbody', 'tfoot']);

/**
 * Escape a run of text, leaving entities that are already there alone.
 *
 * Double-escaping is the bug this avoids: the input has been through a browser already, so it
 * arrives with `&amp;` in it, and a naive `replace(/&/g, '&amp;')` renders `&amp;amp;` to the
 * reader. Only a bare ampersand — one that does not begin a valid entity — is escaped.
 */
function escapeText(s: string): string {
  return s
    .replace(/&(?!#\d+;|#x[0-9a-f]+;|[a-z][a-z0-9]*;)/gi, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Every attribute value this file writes goes through here. Quotes included, deliberately.
 *
 * The ampersand rule is the same entity-aware one `escapeText` uses, and for a sharper reason.
 * An href arriving from a browser already carries `&amp;` between its query parameters, so a
 * blanket `replace(/&/g, "&amp;")` turns `?a=1&amp;b=2` into `?a=1&amp;amp;b=2` and the link now
 * points somewhere that does not exist. That is not a cosmetic bug: it silently breaks every URL
 * with more than one parameter, which is most of the ones anybody would paste.
 */
function escapeAttr(s: string): string {
  return s
    .replace(/&(?!#\d+;|#x[0-9a-f]+;|[a-z][a-z0-9]*;)/gi, '&amp;')
    .replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * A URL that may be linked, or null.
 *
 * ALLOWLIST, NOT BLOCKLIST — `javascript:` is not the only dangerous scheme, it is merely the
 * famous one. `data:` can carry a whole HTML document; `vbscript:` still resolves in some
 * environments; and the next one will have a name nobody here has heard. So three schemes are
 * named and everything else with a colon before its first slash is refused.
 *
 * The flattening is not decoration. Browsers ignore tabs, newlines, NULs and a long tail of
 * invisible Unicode inside a scheme, so a `javascript:` URL with a newline wedged into the middle
 * of the word is a working javascript URL that a naive `startsWith` test reads as a relative path.
 * Rather than enumerate what to strip — a list that is never finished — the scheme test runs
 * against printable ASCII only. The ORIGINAL is what gets stored, because silently rewriting
 * somebody's URL is its own kind of wrong.
 */
export function safeHref(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  const flat = v.replace(/[^\x21-\x7e]/g, '').toLowerCase();
  if (/^(https?:|mailto:)/.test(flat)) return v;
  /* Fragments, absolute and relative paths carry no scheme and cannot smuggle one. */
  if (/^(#|\/|\.\/|\.\.\/)/.test(flat)) return v;
  /* Anything else with a scheme is refused, named or not. */
  if (/^[a-z0-9+.-]*:/.test(flat)) return null;
  /* A bare host — "ffl.timbrown.xyz" — is the one thing people type without a scheme. */
  return /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}([/?#]|$)/.test(flat) ? `https://${v}` : null;
}

/**
 * Which marks an element actually applies, reading its tag AND its inline style.
 *
 * THIS IS THE GOOGLE DOCS FUNCTION, and it exists because of two things Docs does that no other
 * source does.
 *
 * First, it does not use `<b>` and `<i>` at all. Every mark is an inline style on a `<span>` —
 * `font-weight:700`, `font-style:italic`, `text-decoration:underline`. An allowlist that unwraps
 * spans and strips styles, which is the correct instinct, throws away every piece of formatting
 * in the paste and leaves flat text.
 *
 * Second, and worse, Docs wraps THE ENTIRE DOCUMENT in a bold tag switched off by a style —
 * `<b style="font-weight:normal">`. Strip the style, keep the tag, and the whole paste arrives
 * bold. That is not a lossy paste, it is a wrong one, and it is why the style has to be read
 * before any decision is made about the tag.
 *
 * So a style value can ADD a mark the tag did not carry, and can REMOVE one it did. Nothing from
 * the style is ever written back out — only which of four fixed tags to emit — so there is no way
 * for a crafted style value to reach the document.
 */
function marksFor(tag: string, style: string): Set<Mark> {
  const out = new Set<Mark>();
  const own = TAG_MARK[tag];
  if (own) out.add(own);
  if (!style) return out;

  const css = style.toLowerCase();
  const prop = (name: string): string | null => {
    const m = new RegExp(`(?:^|;)\\s*${name}\\s*:([^;]*)`).exec(css);
    return m ? m[1]!.trim() : null;
  };

  const weight = prop('font-weight');
  if (weight !== null) {
    if (/^(bold(er)?|[6-9]00|1000)$/.test(weight)) out.add('b'); else out.delete('b');
  }
  const fontStyle = prop('font-style');
  if (fontStyle !== null) {
    if (/^(italic|oblique)/.test(fontStyle)) out.add('i'); else out.delete('i');
  }
  const deco = prop('text-decoration-line') ?? prop('text-decoration');
  if (deco !== null) {
    if (deco.includes('underline')) out.add('u'); else out.delete('u');
    if (deco.includes('line-through')) out.add('s'); else out.delete('s');
  }
  return out;
}

/** One attribute's value, single or double quoted or bare. Word uses one quote, Docs the other. */
function attrOf(attrs: string, name: string): string {
  const m = new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i').exec(attrs);
  return m ? (m[2] ?? m[3] ?? m[4] ?? '') : '';
}

/** A span attribute, as a number the renderer can trust. Anything odd collapses to 1. */
const spanOf = (v: string): number => {
  const n = /^\d{1,2}$/.test(v.trim()) ? Number(v.trim()) : 1;
  return n > 1 ? n : 1;
};

/**
 * One source element, and the canonical tags it caused to be opened.
 *
 * A frame is pushed for EVERY element, including the ones that survive nothing — a `<span>` with
 * no marks, an `<img>`, the neutralised `<b>` above. That is what makes the close tag work: the
 * closer has to pop exactly what its opener pushed, and for most of a Google Docs paste that is
 * nothing at all.
 */
interface Frame { src: string; emitted: string[] }

/**
 * Where a tag actually ends, respecting quoted attribute values.
 *
 * `indexOf('>')` is wrong and it took a `data:` URL to show it: an href can legitimately contain
 * a bracket, and `<a href="data:text/html,<script>...">` ends the tag four characters into its own
 * attribute. Nothing dangerous survived that, because the fragments left over are escaped as
 * text, but the paste came out as visible rubbish rather than as a refused link.
 */
function tagEnd(s: string, from: number): number {
  let quote: string | null = null;
  for (let k = from + 1; k < s.length; k += 1) {
    const c = s[k]!;
    if (quote) { if (c === quote) quote = null; continue; }
    if (c === '"' || c === "'") { quote = c; continue; }
    if (c === '>') return k;
  }
  return -1;
}


/**
 * Reduce arbitrary HTML to the tags above, balanced.
 *
 * Runs on every save and again on every render. Twice is deliberate: sanitising on save protects
 * the database, sanitising on render protects the reader from anything that reached the database
 * another way - a hand-edited Firestore document, an import, a bug in a future writer.
 */
export function sanitizeRich(input: string): string {
  if (!input) return '';

  const out: string[] = [];
  const stack: Frame[] = [];
  let i = 0;

  const closeFrame = (f: Frame) => {
    for (let k = f.emitted.length - 1; k >= 0; k -= 1) out.push(`</${f.emitted[k]}>`);
  };
  const popTo = (at: number) => { while (stack.length > at) closeFrame(stack.pop()!); };
  const closeTo = (src: string) => {
    const at = stack.map((f) => f.src).lastIndexOf(src);
    if (at < 0) return false;
    popTo(at);
    return true;
  };
  /** Close back past the innermost frame that opened `tag`. */
  const closeTag = (tag: string) => {
    const at = stack.map((f) => f.emitted.includes(tag)).lastIndexOf(true);
    if (at >= 0) popTo(at);
  };
  const openTags = () => stack.flatMap((f) => f.emitted);
  /** A wrapper the source never wrote. Its `src` matches no closing tag, by design. */
  const implicit = (tag: string) => {
    stack.push({ src: ` ${tag}`, emitted: [tag] });
    out.push(`<${tag}>`);
  };

  while (i < input.length) {
    const lt = input.indexOf('<', i);
    if (lt < 0) { out.push(escapeText(input.slice(i))); break; }
    if (lt > i) out.push(escapeText(input.slice(i, lt)));

    // Comments and doctypes carry '>' inside them, so they cannot be found by indexOf('>').
    if (input.startsWith('<!--', lt)) {
      const end = input.indexOf('-->', lt + 4);
      i = end < 0 ? input.length : end + 3;
      continue;
    }
    if (input.startsWith('<!', lt)) {
      const end = input.indexOf('>', lt);
      i = end < 0 ? input.length : end + 1;
      continue;
    }

    /*
     * A bare `<` in prose is text, not a tag. "fewer than 8 represented" gets written `< 8` all
     * the time, and treating it as a tag start sends the scanner hunting for a closing bracket
     * that belongs to something else entirely.
     */
    if (!/^<\/?[a-z]/i.test(input.slice(lt, lt + 3))) {
      out.push('&lt;');
      i = lt + 1;
      continue;
    }

    const gt = tagEnd(input, lt);
    if (gt < 0) { out.push(escapeText(input.slice(lt))); break; }

    const raw = input.slice(lt + 1, gt);
    i = gt + 1;

    const closing = raw.startsWith('/');
    const inner = closing ? raw.slice(1) : raw;
    const name = inner.trim().split(/[\s/]/)[0]!.toLowerCase();
    if (!name) continue;

    if (OPAQUE.has(name)) {
      if (!closing) {
        const end = input.toLowerCase().indexOf(`</${name}`, i);
        const done = end < 0 ? input.length : input.indexOf('>', end);
        i = done < 0 ? input.length : done + 1;
      }
      continue;
    }

    if (closing) { closeTo(name); continue; }

    const attrs = inner.slice(name.length);
    const selfClosing = /\/\s*$/.test(attrs);
    const struct = STRUCT[name];

    if (struct === 'br') { out.push('<br>'); continue; }

    const emitted: string[] = [];
    const opens: string[] = [];

    /*
     * TABLE STRUCTURE IS REPAIRED, NOT TRUSTED. A cell pasted on its own, a `<tr>` that lost its
     * table, a `<table>` that goes straight to `<tr>` - all three turn up in real pastes, and all
     * three make React refuse the nesting rather than render it. The missing ancestors are
     * opened, rather than the content being dropped on the floor.
     */
    if (struct === 'tr') {
      if (openTags().includes('tr')) closeTag('tr');
      if (!openTags().includes('table')) implicit('table');
      if (!openTags().some((t) => SECTION.has(t))) implicit('tbody');
    }
    if (struct && CELL.has(struct)) {
      if (openTags().some((t) => CELL.has(t))) {
        const at = stack.map((f) => f.emitted.some((t) => CELL.has(t))).lastIndexOf(true);
        popTo(at);
      }
      if (!openTags().includes('tr')) {
        if (!openTags().includes('table')) { implicit('table'); implicit('tbody'); }
        implicit('tr');
      }
    }

    /*
     * A `<li>` with no list around it is what a browser hands you when somebody pastes one bullet
     * out of a document. Dropping it loses the line; keeping it bare renders an unmarked
     * paragraph. Opening a list around it is the only reading that preserves what was meant.
     */
    if (struct === 'li' && !openTags().some((t) => t === 'ul' || t === 'ol')) implicit('ul');
    /* `<p>` inside `<p>` is a browser artefact, never an intention. */
    if (struct && BLOCK.has(struct) && openTags().includes(struct)) closeTag(struct);

    if (struct === 'a') {
      const href = safeHref(attrOf(attrs, 'href'));
      /* An unapproved href does not fail the paste - the link is unwrapped to its own text. */
      if (href) { emitted.push('a'); opens.push(`<a href="${escapeAttr(href)}">`); }
    } else if (struct && ALLOWED.has(struct)) {
      emitted.push(struct);
      if (CELL.has(struct)) {
        const c = spanOf(attrOf(attrs, 'colspan'));
        const r = spanOf(attrOf(attrs, 'rowspan'));
        opens.push(`<${struct}${c > 1 ? ` colspan="${c}"` : ''}${r > 1 ? ` rowspan="${r}"` : ''}>`);
      } else opens.push(`<${struct}>`);
    }

    const marks = marksFor(name, attrOf(attrs, 'style'));
    for (const m of MARKS) {
      if (marks.has(m)) { emitted.push(m); opens.push(`<${m}>`); }
    }

    for (const o of opens) out.push(o);
    if (selfClosing) { closeFrame({ src: name, emitted }); continue; }
    stack.push({ src: name, emitted });
  }

  popTo(0);

  return tidy(out.join(''));
}

/** Drop the empty wrappers a contentEditable leaves behind when somebody deletes a line. */
function tidy(html: string): string {
  let s = html;
  let prev = '';
  /*
   * "EMPTY" HAS TO INCLUDE A NON-BREAKING SPACE, and that is not a detail. A blank line in Google
   * Docs is not an empty paragraph - it is a paragraph containing `&nbsp;`, because a truly empty
   * one would collapse in the Doc too. Treating that as content keeps every blank line the writer
   * used to separate his sections, and the reader then adds its own paragraph spacing on top, so
   * a normally-spaced document arrives double-spaced. The gap is the writer's blank line plus our
   * margin plus the blank line's own margin, which is why it reads as three lines rather than two.
   *
   * Note this is the same rule already applied to `<p><br></p>`, which is what pressing Enter
   * twice in the editor produces. Blank paragraphs have never survived a save here; Docs simply
   * spells them differently.
   */
  const BLANK = '(?:\\s|<br>|&nbsp;|&#160;|\u00a0)*';
  const empties = new RegExp(`<(p|li|ul|ol|b|i|u|s|a)(?: [^>]*)?>${BLANK}</\\1>`, 'g');
  while (s !== prev) {
    prev = s;
    s = s.replace(empties, '');
  }
  /*
   * GOOGLE DOCS SEPARATES BLOCKS WITH A BARE `<br>`, and this is the whole of the extra-whitespace
   * bug. Not a blank paragraph - a `<br>` sitting directly between a `</p>` and the next `<p>`.
   * The real payload from Tim's proposal had ELEVEN of them.
   *
   * Left in, they do not merely add a line. `document.execCommand('insertHTML')` normalises a bare
   * block-level `<br>` into `<p><br></p>`, so each one becomes an empty paragraph carrying its own
   * margins - which is why the gap read as three line-heights rather than two, and why stripping
   * empty paragraphs did not fix it: the browser created them AFTER the sanitiser had run.
   *
   * A `<br>` INSIDE a paragraph is a real line break and is left alone. That is what `plainToRich`
   * emits for a single newline, and what Shift+Enter produces in the editor.
   */
  const BC = '</(?:p|ul|ol|li|table|thead|tbody|tfoot|tr|th|td)>';
  const BO = '<(?:p|ul|ol|li|table|thead|tbody|tfoot|tr|th|td)(?: [^>]*)?>';
  const afterBlock = new RegExp(`(${BC})(?:<br>)+`, 'g');
  const beforeBlock = new RegExp(`(?:<br>)+(${BO})`, 'g');
  prev = '';
  while (s !== prev) {
    prev = s;
    s = s.replace(afterBlock, '$1').replace(beforeBlock, '$1');
  }

  /*
   * Google Docs wraps every list item's and every table cell's text in a paragraph. Left alone,
   * each bullet and each cell carries a paragraph's margin, so a three-item list arrives
   * double-spaced and a table gains a blank line inside every box.
   */
  s = s.replace(/<(li|td|th)((?: [^>]*)?)><p>/g, '<$1$2>').replace(/<\/p><\/(li|td|th)>/g, '</$1>');
  /* A table with every cell deleted is not a table. An EMPTY CELL is left alone - it is a cell. */
  s = s.replace(/<table>(?:<(?:thead|tbody|tfoot)>(?:<tr><\/tr>)*<\/(?:thead|tbody|tfoot)>)*<\/table>/g, '');
  return s.replace(/^(?:<br>)+/, '').replace(/(?:<br>)+$/, '').trim();
}

/** True when the value is formatted markup rather than one of the old plain-text bodies. */
export function isRich(s: string): boolean {
  return /<\/?(?:p|br|b|i|u|s|ul|ol|li|a|table|thead|tbody|tfoot|tr|th|td|strong|em|strike|del|ins|div|span)\b[^>]*>/i
    .test(s);
}

/**
 * An old plain-text body, as markup.
 *
 * Every proposal written before this existed is plain text with meaningful blank lines, and the
 * editor has to open them without mangling them. A blank line is a paragraph; a single newline
 * inside one is a hard break, which is what "Section 1:" on its own line has always meant here.
 */
export function plainToRich(text: string): string {
  if (!text.trim()) return '';
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeText(para.trim()).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

const ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'", '#160': ' ',
};

const decode = (s: string): string =>
  s.replace(/&(#?\w+);/g, (m, e: string) => ENTITIES[e.toLowerCase()] ?? m);

/**
 * Markup back to plain text, for the places that cannot render it.
 *
 * The spreadsheet export, the printed minutes and the search index all want a string, and all
 * three want the STRUCTURE to survive the trip: a bulleted list flattened to one run-on line, or
 * a table flattened to a wall of words, is the thing this whole change exists to stop happening.
 * Cells are tab-separated, which is what a spreadsheet expects and what a terminal aligns.
 *
 * A link keeps its URL in brackets. Losing it entirely would be the one lossy conversion nobody
 * could reconstruct: the reader of the minutes cannot click, but he can type.
 */
export function richToPlain(html: string): string {
  if (!html) return '';
  if (!isRich(html)) return html;
  return html
    .replace(/<a href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_m, href: string, text: string) => {
      const url = decode(href);
      const inner = decode(text.replace(/<[^>]+>/g, ''));
      return inner.trim() === url ? url : `${inner} (${url})`;
    })
    .replace(/<li\b[^>]*>/gi, '\n\u2022 ')
    .replace(/<br\b[^>]*>/gi, '\n')
    .replace(/<\/(th|td)>/gi, '\t')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<\/(p|ul|ol|table)>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&(#?\w+);/g, (m, e: string) => ENTITIES[e.toLowerCase()] ?? m)
    .replace(/\t+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ ]+\n/g, '\n')
    .trim();
}

/** Whether there is anything in it. `<p><br></p>` is what an "empty" contentEditable holds. */
export function isEmptyRich(html: string): boolean {
  return richToPlain(sanitizeRich(html)).trim().length === 0;
}

/* -- reading it back as a tree ---------------------------------------------------------------- */

export type RichTag =
  | 'p' | 'b' | 'i' | 'u' | 's' | 'ul' | 'ol' | 'li' | 'br' | 'a'
  | 'table' | 'thead' | 'tbody' | 'tfoot' | 'tr' | 'th' | 'td';

export type RichNode = string | {
  tag: RichTag;
  children: RichNode[];
  href?: string;
  colSpan?: number;
  rowSpan?: number;
};

/**
 * Sanitised markup as a tree of nodes, for rendering.
 *
 * WHY A TREE RATHER THAN `dangerouslySetInnerHTML`. Two reasons, and the second is the one that
 * decided it. The first is that the escape hatch would then be the ONLY thing standing between a
 * bad row in Firestore and script execution, and a sanitiser is a much better guarantee when
 * nothing downstream can execute markup anyway. The second is `Linkify`: it walks the React tree
 * turning rule ids in prose into section marks, it can only see STRINGS, and an `innerHTML` blob
 * is opaque to it. A proposal that cites 2019-I-2 in its second paragraph should get the same chip
 * there as it gets everywhere else in the app, and this is what keeps that true.
 *
 * It parses the SANITISED string, whose shape this file controls exactly, which is why the
 * attribute reading below can be as simple as it is.
 */
export function parseRich(html: string): RichNode[] {
  const clean = sanitizeRich(html);
  const root: RichNode[] = [];
  const stack: Extract<RichNode, { tag: RichTag }>[] = [];
  const put = (n: RichNode) => (stack.length ? stack[stack.length - 1]!.children : root).push(n);
  const pop = () => {
    const done = stack.pop();
    if (done) (stack.length ? stack[stack.length - 1]!.children : root).push(done);
  };

  let i = 0;
  while (i < clean.length) {
    const lt = clean.indexOf('<', i);
    if (lt < 0) { if (i < clean.length) put(decode(clean.slice(i))); break; }
    if (lt > i) put(decode(clean.slice(i, lt)));
    const gt = clean.indexOf('>', lt);
    if (gt < 0) break;
    const raw = clean.slice(lt + 1, gt);
    i = gt + 1;
    const closing = raw.startsWith('/');
    const body = closing ? raw.slice(1) : raw;
    const tag = body.split(/[\s>]/)[0] as RichTag;
    if (tag === 'br') { put({ tag, children: [] }); continue; }
    if (closing) { pop(); continue; }
    const node: Extract<RichNode, { tag: RichTag }> = { tag, children: [] };
    if (tag === 'a') node.href = decode(attrOf(body, 'href'));
    if (tag === 'th' || tag === 'td') {
      const c = Number(attrOf(body, 'colspan') || 1);
      const r = Number(attrOf(body, 'rowspan') || 1);
      if (c > 1) node.colSpan = c;
      if (r > 1) node.rowSpan = r;
    }
    stack.push(node);
  }
  while (stack.length) pop();
  return root;
}
